
import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
// import { InfoGrid } from '../../utils/reportUtils';
import { convertUnderscoreToTitle } from '../../../utils/commonUtils';
import { InfoGrid } from '../../../utils/reportUtils';
import {  DonorSourceType, Medication,Event, Hormone, semenProcessingType, OocyteRetrievalType } from './CycleOverviewReportHelper';
import DataTable from './MedicationTable';
import { CycleDetailsType } from '../CycleDetailsHelper';
import { EmbryoTransferDetail } from '../EmbryoTransferOutcome/EmbryoTransferOutcomeHelper';
import { formatDate } from '../../../utils/dateUtils';

interface CycleOverviewReportProps {
  cycleOverviewReportData: CycleDetailsType | null;
}

const CycleOverviewReport: React.FC<CycleOverviewReportProps> = ({ cycleOverviewReportData }) => {
  const { t } = useTranslation('cycleOverview_config');
  const { t: tGeneral } = useTranslation('general');




  // Medications
  const stimulationMeds = cycleOverviewReportData?.stimulation_chart?.medications?.filter(
    (m) => m.drug_type === 'STIMULATION'
  ) ?? [];
  const triggerMeds = cycleOverviewReportData?.stimulation_chart?.medications?.filter(
    (m) => m.drug_type === 'TRIGGER'
  ) ?? [];

  // Events
  const events = cycleOverviewReportData?.stimulation_chart?.events ?? [];
  //Hormones
  const hormones = cycleOverviewReportData?.stimulation_chart?.hormones ?? [];
  const semenProcessingData = cycleOverviewReportData?.semen_processing ?? [];
  const oocyteRetrievalData = cycleOverviewReportData?.oocyte_retrievals ?? [];
  const embryoTransferOutcomeData = cycleOverviewReportData?.embryo_transfer_details ?? [];

  // Columns for medications
  const medicationColumns = [
    { label: t('Drug Name'), render: (med: Medication) => med.drug?.name_label || '-' },
    { label: t('Molecule'), render: (med: Medication) => med.drug?.molecule_label || '-' },
    { label: t('Strength'), render: (med: Medication) => med.drug?.strength || '-' },
    { label: t('UOM'), render: (med: Medication) => med.drug?.stock_uom_label || '-' },
    { label: t('Quantity'), render: (med: Medication) => med.quantity || '-' },
    { label: t('Start Date'), render: (med: Medication) => formatDate(med.start_date) || '-' },
    { label: t('End Date'), render: (med: Medication) => formatDate(med.end_date) || '-' },
    { label: t('Start Time'), render: (med: Medication) => med.start_time || '-' },
  ];

  // Columns for events
  const eventColumns = [
    { label: t('Event Type'), render: (event: Event) => event.event_type },
    { label: t('Date'), render: (event: Event) => formatDate(event.date) || '-' },
    { label: t('Remark'), render: (event: Event) => event.appointment?.remark || '-' },
    { label: t('Status'), render: (event: Event) => event.appointment?.status || '-' },
  ];

  //coluns for hormones
  const hormoneColumns = [
    { label: t('Hormone Name'), render: (hormone: Hormone) => hormone.laboratory_test?.name || '-' },
    { label: t('Hormone Value'), render: (hormone: Hormone) => hormone.results || '-' },
    { label: t('Units'), render: (hormone: Hormone) => hormone.laboratory_test?.unit || '-' },
    { label: t('Date'), render: (hormone: Hormone) => formatDate(hormone.date) || '-' },
  ];
  //columns for SemenProcessing
  const semenProcessingColumns = [
    { label: t('Cycle Number'), render: (event: semenProcessingType) => event.cycle_number },
    { label: t('Sample Number'), render: (event: semenProcessingType) => event.sample_number },
    { label: t('Sperm Type'), render: (event: semenProcessingType) => event.sperm_type_label },
    { label: t('Disposition Type'), render: (event: semenProcessingType) => event.disposition_type },
    { label: t('Semen Preparation Method'), render: (event: semenProcessingType) => event.semen_preparation_method_label },
    { label: t('Sample Collected At'), render: (event: semenProcessingType) => event.sample_collected_at },
    { label: t('Done By'), render: (event: semenProcessingType) => event.done_by_name },
    { label: t('Witness Name'), render: (event: semenProcessingType) => event.witness_name },
    { label: t('Source Name'), render: (event: semenProcessingType) => event.source_name },
  ];
  const oocyteRetrievalColumns = [
    { label: t('Retrieval Date'), render: (item: OocyteRetrievalType) => formatDate(item.opu_date) || '-' },
    { label: t('Retrieval Time'), render: (item: OocyteRetrievalType) => item.start_time },
    { label: t('Clinician'), render: (item: OocyteRetrievalType) => item.clinician_name },
    { label: t('Embryologist Name'), render: (item: OocyteRetrievalType) => item.embryologist_name },
    { label: t('Nurse'), render: (item: OocyteRetrievalType) => item.nurse },
    { label: t('Left Optimum Follicles'), render: (item: OocyteRetrievalType) => item.optimum_follicles?.left_follicles ?? '-' },
    { label: t('Right Optimum Follicles'), render: (item: OocyteRetrievalType) => item.optimum_follicles?.right_follicles ?? '-' },
    { label: t('Left Oocytes Retrieved'), render: (item: OocyteRetrievalType) => item.oocytes_retrieved_left },
    { label: t('Right Oocytes Retrieved'), render: (item: OocyteRetrievalType) => item.oocytes_retrieved_right },
    { label: t('Level Of Difficulty'), render: (item: OocyteRetrievalType) => item.difficulty_level_label },
  ];

  const embryoTransferColumns = [
    { label: t('EmbryoNo'), render: (item: EmbryoTransferDetail) => item.embryo_number },
    { label: t('InseminationTechnique'), render: (item: EmbryoTransferDetail) => item.insemination_technique },
    { label: t('CellStage'), render: (item: EmbryoTransferDetail) => convertUnderscoreToTitle(item.stage) || '-' },
    { label: t('Day'), render: (item: EmbryoTransferDetail) => item.day || '-' },
    { label: t('Grade'), render: (item: EmbryoTransferDetail) => item.grade || '-' },
    { label: t('PGTResult'), render: (row: EmbryoTransferDetail) =>
      row?.pgt_results?.pgt_results && row?.pgt_results?.pgt_result_embryo_sex
        ? `${convertUnderscoreToTitle(row.pgt_results.pgt_results)} (${row.pgt_results.pgt_result_embryo_sex || '-'})`
        : '-'
    },
    {label:t('BetaHCG'), render: (row: EmbryoTransferDetail) =>
      row.outcome && row.outcome[0]?.outcome_details ? (
        <div style={{ color: '#A0A0A0', fontSize: '14px', lineHeight: '20px' }}>
          <div>Value - {convertUnderscoreToTitle(row.outcome[0].outcome_details[0].status) || '-'}</div>
          <div>Result - {row.outcome[0].outcome_details[0].result || '-'}</div>
          <div>Date - {formatDate(row.outcome[0].outcome_details[0].embryo_transfer_date) || '-'}</div>
        </div>
      ) : (
        '-'
      )
    },
    {label:t('USG'), render: (row: EmbryoTransferDetail) =>
      row.outcome && row.outcome[0] ? (
        <div style={{ color: '#A0A0A0', fontSize: '14px', lineHeight: '20px' }}>
          <div>No of Sacs - {row.outcome[0].no_of_sacs || '-'}</div>
          <div>Delivery type - {convertUnderscoreToTitle(row.outcome[0].delivery_type) || '-'}</div>
          <div>Pregnacy type - {convertUnderscoreToTitle(row.outcome[0].pregnancy_type) || '-'}</div>
        </div>
      ) : (
        '-'
      )
    },
    {label:t('PregnancyOutcome'), render: (row: EmbryoTransferDetail) => (
      <div style={{ color: '#A0A0A0', fontSize: '14px', lineHeight: '20px' }}>
        <div>{row.outcome && row.outcome[0]?.birth_details?.length > 0 ? 'Yes' : (row.outcome ? 'No' : '-')}</div>
        <div>{row.outcome && row.outcome[0]?.observation_date ? formatDate(row.outcome[0]?.observation_date) : ''}</div>
        <div>{convertUnderscoreToTitle(row.outcome && row.outcome[0]?.birth_details[0]?.outcome_type ? row.outcome[0]?.birth_details[0]?.outcome_type : '')}</div>
      </div>
    )}
  ];


  const cycleOverviewGridData: string[][] = [
    // Row 1
    [
      t('CycleNumber'),
      cycleOverviewReportData?.cycle_number || '-',
      t('CycleName'),
      cycleOverviewReportData?.cycle_name_label || '-',
    ],

    // Row 2
    [
      convertUnderscoreToTitle(t('Indication')),
      cycleOverviewReportData?.procedure?.indication_label || '-',
      t('OocyteSource'),
      cycleOverviewReportData?.procedure?.donor_info?.oocyte_source?.length
        ? cycleOverviewReportData.procedure.donor_info.oocyte_source
          .map((source: DonorSourceType) => `${source.first_name || ''} ${source.last_name || ''}`.trim())
          .join(', ')
        : '-',
    ],

    // Row 3
    [
      t('SpermSource'),
      cycleOverviewReportData?.procedure?.donor_info?.sperm_source?.length
        ? cycleOverviewReportData.procedure.donor_info.sperm_source
          .map((source: DonorSourceType) => `${source.first_name || ''} ${source.last_name || ''}`.trim())
          .join(', ')
        : '-',
      t('Recipient'),
      cycleOverviewReportData?.procedure?.donor_info?.recipient
        ? `${cycleOverviewReportData.procedure.donor_info.recipient.first_name || ''} ${cycleOverviewReportData.procedure.donor_info.recipient.last_name || ''}`.trim()
        : '-',
    ],

    // Row 4
    [
      t('MedicationProtocol'),
      cycleOverviewReportData?.medication_protocol_name_label || '-',
      t('CycleCreator'),
      cycleOverviewReportData?.cycle_creator_name || '-',
    ],

    // Row 5
    [
      t('CycleCoordinator'),
      cycleOverviewReportData?.coordinator_name || '-',
      t('CycleProvider'),
      cycleOverviewReportData?.provider_name || '-',
    ],

    // Row 6
    [
      t('CycleStartDate'),
      formatDate(cycleOverviewReportData?.cycle_start_date) || '-',
      t('CycleEndDate'),
      formatDate(cycleOverviewReportData?.cycle_end_date) || '-',
    ],

    // Row 7
    [
      t('LMPDate'),
      formatDate(cycleOverviewReportData?.lmp_date) || '-',
      t('Remark'),
      cycleOverviewReportData?.remarks || '-',
    ],
  ];

  return (
    <>
      <Box sx={{ mb: 1, mx: 1,p:2.5, pt:0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>{t('CycleDetails')}</Typography>
        <InfoGrid data={cycleOverviewGridData} />
      </Box>
      
      <Box sx={{ mb: 1, mx: 1, p: 2.5, pt: 0 }}>
        <Typography variant="h6" sx={{ mb: 1, color: '#444' }}>{tGeneral('Stimulation_Details')}</Typography>
        {events.length > 0 && (
          <DataTable rows={events} columns={eventColumns} title={tGeneral('Events')} />
        )}
        {stimulationMeds.length > 0 && (
          <DataTable rows={stimulationMeds} columns={medicationColumns} title={tGeneral('StimMedications')} />
        )}
        {triggerMeds.length > 0 && (
          <DataTable rows={triggerMeds} columns={medicationColumns} title={tGeneral('TriggerMedications')} />
        )}
        {hormones.length > 0 && (
          <DataTable rows={hormones} columns={hormoneColumns} title={tGeneral('Hormones')} />
        )}
      </Box>
      <Box sx={{ mb: 1, mx: 1, p: 2.5, pt: 0 }}>
        <Typography variant="h6" sx={{ mb: 1, color: '#444' }}>{tGeneral('SemenProcessing_Details')}</Typography>
        {semenProcessingData.length > 0 && (
          <DataTable rows={semenProcessingData} columns={semenProcessingColumns}  />
        )}
      </Box>
      <Box sx={{ mb: 1, mx: 1, p: 2.5, pt: 0 }}>
        <Typography variant="h6" sx={{ mb: 1, color: '#444' }}>{tGeneral('OocyteRetrieval_Details')}</Typography>
        {oocyteRetrievalData.length > 0 && (
          <DataTable rows={oocyteRetrievalData} columns={oocyteRetrievalColumns}  />
        )}
      </Box>
      <Box sx={{ mb: 1, mx: 1, p: 2.5, pt: 0 }}>
        <Typography variant="h6" sx={{ mb: 1, color: '#444' }}>{tGeneral('EmbryoTransfer_Outcome')}</Typography>
        {embryoTransferOutcomeData.length > 0 && (
          <DataTable rows={embryoTransferOutcomeData} columns={embryoTransferColumns}  />
        )}
      </Box>
      
    </>
  );
};

export default CycleOverviewReport;
